/**
 * Panel de topes de producción semanal.
 *
 * Tope GLOBAL (unidades/semana para toda la planta) + topes opcionales por
 * FORMATO. El más restrictivo manda. Oportunidades, "Proponer plan" y
 * "Sugerir máximo" nunca lo superan; el plan manual que lo exceda se marca
 * en ámbar (no se bloquea). Persistido en localStorage por empresa.
 */
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

export interface PlanCaps {
  global: number | null
  formatos: Record<string, number>
}

export const EMPTY_CAPS: PlanCaps = { global: null, formatos: {} }

export function hasCaps(caps: PlanCaps): boolean {
  return (caps.global ?? 0) > 0 || Object.values(caps.formatos).some((v) => v > 0)
}

interface Props {
  dark: boolean
  caps: PlanCaps
  formatos: string[]
  onChange: (caps: PlanCaps) => void
  onClose: () => void
}

function parseCap(text: string): number | null {
  const clean = text.replace(/[.\s]/g, '').replace(',', '.')
  if (clean === '') return null
  const n = Number(clean)
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null
}

export function CapsPanel({ dark, caps, formatos, onChange, onClose }: Props) {
  const { t } = useTranslation()

  const cardCls = dark
    ? 'rounded-xl border border-amber-500/25 bg-slate-900'
    : 'rounded-xl border border-amber-300 bg-white shadow-sm'
  const inputCls = clsx(
    'w-28 text-right text-[11px] px-2 py-1 rounded-lg border outline-none tabular-nums transition-colors',
    dark
      ? 'border-white/10 bg-slate-950 text-slate-200 focus:border-amber-500 placeholder:text-slate-600'
      : 'border-slate-200 bg-white text-slate-700 focus:border-amber-400 placeholder:text-slate-300',
  )
  const labelCls = clsx('text-[11px] font-semibold', dark ? 'text-slate-300' : 'text-slate-600')

  const setGlobal = (text: string) => onChange({ ...caps, global: parseCap(text) })
  const setFormato = (formato: string, text: string) => {
    const v = parseCap(text)
    const next = { ...caps.formatos }
    if (v === null) delete next[formato]
    else next[formato] = v
    onChange({ ...caps, formatos: next })
  }

  return (
    <div className={cardCls}>
      <div className={clsx('flex items-center gap-2 px-4 py-2.5 border-b', dark ? 'border-white/8' : 'border-slate-100')}>
        <span className="text-[13px]">⚙️</span>
        <div className="flex-1 min-w-0">
          <div className={clsx('text-[12px] font-bold', dark ? 'text-slate-100' : 'text-slate-800')}>
            {t('planFormato.topeTitulo')}
          </div>
          <div className={clsx('text-[10px]', dark ? 'text-slate-500' : 'text-slate-400')}>
            {t('planFormato.topeSub')}
          </div>
        </div>
        {hasCaps(caps) && (
          <button
            onClick={() => onChange(EMPTY_CAPS)}
            className={clsx('shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-lg border transition-colors cursor-pointer', dark ? 'border-white/15 text-slate-400 hover:bg-white/10' : 'border-slate-200 text-slate-500 hover:bg-slate-50')}
          >
            {t('planFormato.topeQuitar')}
          </button>
        )}
        <button
          onClick={onClose}
          className={clsx('shrink-0 text-[11px] px-2 py-1 rounded-lg transition-colors', dark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100')}
        >
          ✕
        </button>
      </div>

      <div className="px-4 py-3 flex flex-col gap-2 max-h-[260px] overflow-y-auto scroll-thin">
        <div className="flex items-center justify-between gap-3">
          <span className={clsx(labelCls, 'font-bold')}>{t('planFormato.topeGlobal')}</span>
          <input
            type="text"
            inputMode="numeric"
            defaultValue={caps.global != null ? String(caps.global) : ''}
            placeholder={t('planFormato.topePlaceholder')}
            onBlur={(e) => setGlobal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
            className={inputCls}
          />
        </div>
        <div className={clsx('border-t my-1', dark ? 'border-white/8' : 'border-slate-100')} />
        <div className={clsx('text-[9px] font-bold uppercase tracking-wider', dark ? 'text-slate-500' : 'text-slate-400')}>
          {t('planFormato.topePorFormato')}
        </div>
        {formatos.map((f) => (
          <div key={f} className="flex items-center justify-between gap-3">
            <span className={clsx(labelCls, 'truncate')}>{f}</span>
            <input
              type="text"
              inputMode="numeric"
              defaultValue={caps.formatos[f] != null ? String(caps.formatos[f]) : ''}
              placeholder={t('planFormato.topePlaceholder')}
              onBlur={(e) => setFormato(f, e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
              className={inputCls}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
